// Reading an EFP purchase confirmation into fields.
//
// The confirmations are Word tables, so mammoth's raw text puts a label on one line and
// its value on the next - "Contract Date:" then "July 02, 2026". The regexes this
// replaces all required `Label: value` on a SINGLE line, which matches none of the
// fields in that layout. Parsing here is label-driven instead: find a known label, take
// everything after it up to the next known label, and let the value sit on the same line
// or the following ones. Both layouts fall out of the same rule.
//
// Everything is optional on the way out. A confirmation with a blank Contract number or
// no basis is a real thing that arrives, and the caller decides what is missing rather
// than the parser throwing.
import { ContractDate, Currency, Unit } from '../../../../Generated/Commodity-Management-Api';
import {
    UnitReading,
    chooseContractUnit,
    readUnitReadings,
    resolvePrice,
    soleDocumentUnit,
} from './EfpContractPrices';

/** Labels in the confirmation, in no particular order - used to bound each value. */
const LABELS = [
    'Contract Date',
    'Contract Balance Unpriced',
    'Contract',
    'Phone',
    'Seller',
    'Buyer',
    'Plant',
    'Futures Month',
    'Delivery Month',
    'Quantity Priced',
    'Quantity',
    'Commodity',
    'Board Price',
    'Basis',
    'Pricing Date',
    'Net Price',
    'Price',
    'Currency',
    'Requestor',
    'Additional Terms',
    'Additional Notes',
    'Remarks',
    'NOTES',
];

export interface EfpContractFields {
    contractNumber?: string;
    contractDate?: string;
    sellerName?: string;
    buyerName?: string;
    plantName?: string;
    commodityName?: string;
    /** The futures month the EFP is struck against. */
    futuresMonth?: ContractDate;
    /** The delivery month - what the budget date is set from. */
    deliveryMonth?: ContractDate;
    volume?: number;
    unit?: Unit;
    /** Futures/board leg of the price - what the physical position is booked at. */
    boardPrice?: number;
    /**
     * What the board price is quoted per. Undefined when the confirmation gave a bare
     * number and uses more than one unit, so there is no telling - the user says.
     */
    boardPriceUnit?: Unit;
    /** Basis leg. Either stated on its own or the middle term of "board + basis = net". */
    basisPrice?: number;
    /** What the basis is quoted per; same rule as boardPriceUnit. */
    basisPriceUnit?: Unit;
    /** Board + basis, only when the two are in the same unit. */
    netPrice?: number;
    /** What the net price is quoted per. */
    netPriceUnit?: Unit;
    currency?: Currency;
    /** Every number as quoted, kept so the review can show what the form said. */
    quantityReadings?: UnitReading[];
    boardReadings?: UnitReading[];
    basisReadings?: UnitReading[];
    netReadings?: UnitReading[];
}

const MONTH_NAMES = [
    'january', 'february', 'march', 'april', 'may', 'june',
    'july', 'august', 'september', 'october', 'november', 'december',
];

function parseMonthYear(text: string | undefined): ContractDate | undefined {
    if (!text) return undefined;
    // "September 2026", "July  2026", "Sep 26"
    const match = text.match(/([A-Za-z]{3,9})\s+(\d{2,4})/);
    if (!match) return undefined;

    const monthIndex = MONTH_NAMES.findIndex(name => name.startsWith(match[1].toLowerCase()));
    if (monthIndex < 0) return undefined;

    let year = Number(match[2]);
    if (year < 100) year += 2000;

    return { month: monthIndex + 1, year };
}

function parseContractDate(text: string | undefined): string | undefined {
    if (!text) return undefined;
    // "July 02, 2026"
    const match = text.match(/([A-Za-z]{3,9})\s+(\d{1,2}),?\s*(\d{4})/);
    if (!match) return undefined;

    const monthIndex = MONTH_NAMES.findIndex(name => name.startsWith(match[1].toLowerCase()));
    if (monthIndex < 0) return undefined;

    const month = String(monthIndex + 1).padStart(2, '0');
    const day = match[2].padStart(2, '0');
    return `${match[3]}-${month}-${day}`;
}

function parseCurrency(text: string | undefined): Currency | undefined {
    if (!text) return undefined;
    const lower = text.toLowerCase();
    if (lower.includes('canad') || /\bcad\b/.test(lower)) return Currency.Cad;
    if (lower.includes('u.s') || lower.includes('us dollar') || /\busd\b/.test(lower)) return Currency.Usd;
    return undefined;
}

/**
 * Split the document into label -> value. A value runs from its label to the next one,
 * so it does not matter whether the confirmation puts it on the same line or below.
 */
export function extractLabelledValues(text: string): Record<string, string> {
    // Word fills these forms with non-breaking spaces, which would otherwise
    // defeat the whitespace classes in the label patterns.
    const normalized = text.replace(/\r\n/g, '\n').replace(/\u00a0/g, ' ');
    const labelPattern = new RegExp(`^\\s*(${LABELS.map(l => l.replace(/ /g, '\\s+')).join('|')})\\s*:`, 'i');

    const values: Record<string, string> = {};
    let currentLabel: string | null = null;
    let buffer: string[] = [];

    const flush = () => {
        if (currentLabel) {
            const value = buffer.join(' ').replace(/\s+/g, ' ').trim();
            // First occurrence wins: "NOTES" and friends repeat down the form.
            if (!(currentLabel in values)) values[currentLabel] = value;
        }
        buffer = [];
    };

    for (const line of normalized.split('\n')) {
        const match = line.match(labelPattern);
        if (match) {
            flush();
            currentLabel = match[1].replace(/\s+/g, ' ').toLowerCase();
            buffer.push(line.slice(match[0].length));
        } else if (currentLabel) {
            buffer.push(line);
        }
    }
    flush();

    return values;
}

export function parseEfpContract(text: string): EfpContractFields {
    const values = extractLabelledValues(text);
    const get = (label: string) => {
        const value = values[label.toLowerCase()];
        return value && value.trim() ? value.trim() : undefined;
    };

    const quantityReadings = readUnitReadings(get('Quantity'));
    const quantityPricedReadings = readUnitReadings(get('Quantity Priced'));
    const contractUnit = chooseContractUnit(quantityReadings, quantityPricedReadings);

    // Priced quantity first: it is what was actually transacted.
    const volume = resolvePrice(quantityPricedReadings, contractUnit).value
        ?? resolvePrice(quantityReadings, contractUnit).value;

    const priceReadings = readUnitReadings(get('Price'));
    const boardReadings = readUnitReadings(get('Board Price'));
    const basisReadings = readUnitReadings(get('Basis'));
    const netReadings = readUnitReadings(get('Net Price'));

    // What a bare number is per. Only known when the confirmation uses one unit
    // throughout; a form that so much as has a column for a second unit leaves it to
    // the user.
    const soleUnit = soleDocumentUnit(
        get('Quantity'), get('Quantity Priced'), get('Contract Balance Unpriced'),
        get('Price'), get('Board Price'), get('Basis'), get('Net Price')
    );

    // "board + basis = net" in one expression, the only form where the Price block
    // is trusted to hold three separate numbers.
    const sum = (get('Price') ?? '').match(
        /\$?\s*([0-9][0-9,.]*)\s*\+\s*\$?\s*([0-9][0-9,.]*)\s*=\s*\$?\s*([0-9][0-9,.]*)/);
    const fromSum = sum
        ? {
            board: Number(sum[1].replace(/,/g, '')),
            basis: Number(sum[2].replace(/,/g, '')),
            net: Number(sum[3].replace(/,/g, '')),
        }
        : undefined;
    // The expression sits under a unit heading ("PER BUSHEL: $4.22 + $1.55 = $5.77"),
    // which is the unit of all three of its numbers.
    const sumUnit = fromSum
        ? priceReadings.find(reading => reading.value === fromSum.board)?.unit ?? soleUnit
        : undefined;

    /** A stated field's value and unit, falling back to the expression's. */
    const priced = (readings: UnitReading[], fromExpression: number | undefined) => {
        const statedReading = resolvePrice(readings, contractUnit);
        if (statedReading.value !== undefined) {
            // A bare stated number that repeats the expression's is in the expression's unit.
            const unit = statedReading.unit
                ?? (fromExpression !== undefined && statedReading.value === fromExpression ? sumUnit : soleUnit);
            return { value: statedReading.value, unit };
        }
        return fromExpression !== undefined
            ? { value: fromExpression, unit: sumUnit }
            : { value: undefined, unit: undefined };
    };

    // Explicit fields win over the expression: the stated Board Price and Basis are
    // quoted with their units, the expression is not.
    const board = priced(boardReadings, fromSum?.board);
    const stated = priced(basisReadings, fromSum?.basis);
    const net = priced(netReadings, fromSum?.net);

    // A lone number under Price means different things on different forms - the basis on
    // one, the total on another - so it is only consulted when neither Net Price nor
    // Basis was stated, and then it is read as the total. A field labelled just "Price"
    // alongside a separate "Board Price" is the whole price; the form that puts the basis
    // there always states Basis and Net Price too, so this never fires for it.
    const priceFallback = resolvePrice(priceReadings, contractUnit);
    const effectiveNet = net.value !== undefined
        ? net
        : stated.value === undefined
            ? { value: priceFallback.value, unit: priceFallback.unit ?? soleUnit }
            : { value: undefined, unit: undefined };

    // The basis is only derived from net - board when both are known to be per the same
    // thing. Subtracting a per-bushel board from a per-tonne net is what produced
    // nonsense before, so mixed or unknown units leave the basis for the user.
    const canDerive = effectiveNet.value !== undefined
        && board.value !== undefined
        && effectiveNet.unit !== undefined
        && effectiveNet.unit === board.unit;

    const boardPrice = board.value;
    const boardPriceUnit = board.value !== undefined ? board.unit : undefined;

    const basisPrice = stated.value
        ?? (canDerive ? Number((effectiveNet.value! - board.value!).toFixed(6)) : undefined);
    const basisPriceUnit = stated.value !== undefined
        ? stated.unit
        : canDerive ? board.unit : undefined;

    const summable = boardPrice !== undefined && basisPrice !== undefined && boardPriceUnit === basisPriceUnit;
    const resolvedNet = effectiveNet.value ?? (summable ? Number((boardPrice + basisPrice).toFixed(6)) : undefined);
    const netPriceUnit = effectiveNet.value !== undefined ? effectiveNet.unit : summable ? boardPriceUnit : undefined;

    return {
        contractNumber: get('Contract'),
        contractDate: parseContractDate(get('Contract Date')),
        sellerName: get('Seller'),
        buyerName: get('Buyer'),
        plantName: get('Plant'),
        commodityName: get('Commodity'),
        futuresMonth: parseMonthYear(get('Futures Month')),
        deliveryMonth: parseMonthYear(get('Delivery Month')),
        volume,
        unit: contractUnit,
        boardPrice,
        boardPriceUnit,
        basisPrice,
        basisPriceUnit,
        netPrice: resolvedNet,
        netPriceUnit,
        currency: parseCurrency(get('Currency')),
        quantityReadings,
        boardReadings,
        basisReadings,
        netReadings,
    };
}

export interface EfpFieldRequirement {
    key: keyof EfpContractFields;
    label: string;
}

/** What the EFP posting cannot be built without. */
export const REQUIRED_EFP_FIELDS: EfpFieldRequirement[] = [
    { key: 'sellerName', label: 'Seller' },
    { key: 'plantName', label: 'Plant' },
    { key: 'commodityName', label: 'Commodity' },
    { key: 'futuresMonth', label: 'Futures Month' },
    { key: 'deliveryMonth', label: 'Delivery Month' },
    { key: 'volume', label: 'Quantity' },
    { key: 'unit', label: 'Unit' },
    { key: 'boardPrice', label: 'Board Price' },
    { key: 'boardPriceUnit', label: 'Board Price Unit' },
    { key: 'basisPrice', label: 'Basis' },
    { key: 'basisPriceUnit', label: 'Basis Unit' },
];

/** Labels of the fields the confirmation did not yield, for the "what is missing" alert. */
export function getMissingEfpFields(fields: EfpContractFields): string[] {
    return REQUIRED_EFP_FIELDS
        .filter(({ key }) => {
            const value = fields[key];
            return value === undefined || value === null || value === '';
        })
        .map(({ label }) => label);
}
